// schema.org JSON-LD builders shared across pages. Each returns a plain object;
// the <Seo> component serializes + escapes it. Only build these from data that
// is actually visible on the page (best-practice + Google requirement).

const CONTEXT = 'https://schema.org'

// items: [{ name, path? }] in trail order; path is a site-relative path.
export function breadcrumbList(siteUrl, items) {
  return {
    '@context': CONTEXT,
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      ...(it.path ? { item: `${siteUrl}${it.path}` } : {}),
    })),
  }
}

// names ordered innermost→outermost, e.g. [city, state, country]; returns the
// innermost Place with nested containedInPlace, or null.
function containedChain(names) {
  let place = null
  for (let i = names.length - 1; i >= 0; i--) {
    place = {
      '@type': 'Place',
      name: names[i],
      ...(place ? { containedInPlace: place } : {}),
    }
  }
  return place
}

// A tide/current station as a Place with coordinates + location hierarchy.
export function stationPlace(canonicalUrl, station) {
  const place = {
    '@context': CONTEXT,
    '@type': 'Place',
    '@id': `${canonicalUrl}#place`,
    name: station.name,
    url: canonicalUrl,
  }
  if (station.lat != null && station.lng != null) {
    place.geo = {
      '@type': 'GeoCoordinates',
      latitude: station.lat,
      longitude: station.lng,
    }
  }
  // City is intentionally de-emphasized (not shown on the page), so it's left
  // out of the visible content and therefore out of the Place hierarchy too.
  const contained = containedChain([station.state_name, station.country_name].filter(Boolean))
  if (contained) place.containedInPlace = contained
  return place
}

// Breadcrumb trail for a station page: Country › State › Station. The Countries
// index and City levels are intentionally omitted — those pages stay live but
// we don't drive traffic to or emphasize them. `station` carries *_name; `seo`
// carries *_permalink. The last item (the station) has no path. Used for both
// the visible breadcrumb and the BreadcrumbList JSON-LD so they stay identical.
// `pathFor` prefixes a bare path with the active locale (from useLocalePath), so
// the crumb links and the BreadcrumbList JSON-LD both match the localized
// canonical. Defaults to identity for callers that don't localize.
export function stationCrumbs(station, seo = {}, pathFor = (p) => p) {
  return [
    station.country_name &&
      seo.country_permalink && {
        name: station.country_name,
        path: pathFor(`/countries/${seo.country_permalink}`),
      },
    station.state_name &&
      seo.state_permalink && {
        name: station.state_name,
        path: pathFor(`/states/${seo.state_permalink}`),
      },
    { name: station.name },
  ].filter(Boolean)
}
